import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from 'contexts/AuthContext';
import { Loader2, ChevronLeft, Maximize2, Minimize2 } from 'lucide-react';

const ScormPlayer = () => {
  const { moduleId } = useParams();
  const navigate = useNavigate();
  const { api, user } = useAuth();
  const iframeRef = useRef(null);
  const injectedWindowsRef = useRef({ content: null, parent: null }); 
  const [loading, setLoading] = useState(true);
  const [launchData, setLaunchData] = useState(null);
  const [fullscreen, setFullscreen] = useState(false);
  const [status, setStatus] = useState('not attempted');
  const trackingRef = useRef({});

  // Cargar datos de lanzamiento
  useEffect(() => {
    api.get(`/scorm-player/${moduleId}/launch`)
      .then(res => { setLaunchData(res.data); trackingRef.current = res.data.tracking || {}; })
      .catch(() => navigate(-1));
  }, [moduleId]);

  // Inyectar API SCORM en el iframe cuando cargue
  const injectScormApi = () => {
    const iframe = iframeRef.current;
    if (!iframe?.contentWindow) return;

    const tracking = trackingRef.current;
    const saveTracking = async (data) => {
      try {
        await api.post(`/scorm-player/${moduleId}/track`, data);
      } catch {}
    };

    // API SCORM 1.2
    const API = {
      _data: {
        "cmi.core.student_id": user?.id || "",
        "cmi.core.student_name": user?.name || "",
        "cmi.core.lesson_status": tracking.lesson_status || "not attempted",
        "cmi.core.lesson_location": tracking.lesson_location || "",
        "cmi.core.score.raw": tracking.score_raw || "",
        "cmi.core.score.min": "0",
        "cmi.core.score.max": "100",
        "cmi.core.session_time": "00:00:00",
        "cmi.suspend_data": tracking.suspend_data || "",
        "cmi.core.exit": "",
        "cmi.core.entry": tracking.lesson_status === "incomplete" ? "resume" : "ab-initio",
      },
      _initialized: false,
      _finished: false,

      LMSInitialize: function(param) {
        API._initialized = true;
        console.log('[SCORM] LMSInitialize');
        return "true";
      },

      LMSGetValue: function(element) {
        const val = API._data[element] ?? "";
        console.log(`[SCORM] LMSGetValue(${element}) = ${val}`);
        return val;
      },

      LMSSetValue: function(element, value) {
        console.log(`[SCORM] LMSSetValue(${element}, ${value})`);
        API._data[element] = value;

        // Actualizar status en UI
        if (element === "cmi.core.lesson_status") {
          setStatus(value);
        }
        return "true";
      },

      LMSCommit: function(param) {
        console.log('[SCORM] LMSCommit');
        const d = API._data;
        saveTracking({
          lesson_status: d["cmi.core.lesson_status"],
          lesson_location: d["cmi.core.lesson_location"],
          score_raw: d["cmi.core.score.raw"],
          suspend_data: d["cmi.suspend_data"],
          session_time: d["cmi.core.session_time"],
        });
        return "true";
      },

      LMSFinish: function(param) {
        console.log('[SCORM] LMSFinish');
        const d = API._data;
        saveTracking({
          lesson_status: d["cmi.core.lesson_status"],
          lesson_location: d["cmi.core.lesson_location"],
          score_raw: d["cmi.core.score.raw"],
          suspend_data: d["cmi.suspend_data"],
          session_time: d["cmi.core.session_time"],
        });
        API._finished = true;
        return "true";
      },

      LMSGetLastError: function() { return "0"; },
      LMSGetErrorString: function(code) { return "No error"; },
      LMSGetDiagnostic: function(code) { return "No diagnostic"; },
    };

    // También soportar SCORM 2004
    const API_1484_11 = {
      ...API,
      Initialize: API.LMSInitialize.bind(API),
      GetValue: API.LMSGetValue.bind(API),
      SetValue: API.LMSSetValue.bind(API),
      Commit: API.LMSCommit.bind(API),
      Terminate: API.LMSFinish.bind(API),
      GetLastError: API.LMSGetLastError.bind(API),
      GetErrorString: API.LMSGetErrorString.bind(API),
      GetDiagnostic: API.LMSGetDiagnostic.bind(API),
    };

    try {
      iframe.contentWindow.API = API;
      iframe.contentWindow.API_1484_11 = API_1484_11;
      iframe.contentWindow.parent.API = API;
      iframe.contentWindow.parent.API_1484_11 = API_1484_11;

      injectedWindowsRef.current = {
        content: iframe.contentWindow,
        parent: iframe.contentWindow.parent,
      };
    } catch (e) {
      console.warn('[SCORM] No se pudo inyectar API:', e);
    }

    setLoading(false);
  };

  // Limpieza al desmontar el componente (salir del SCORM del todo)
  useEffect(() => {
    return () => {
      const { content, parent } = injectedWindowsRef.current;
      try { if (content) { delete content.API; delete content.API_1484_11; } } catch {}
      try { if (parent)  { delete parent.API;  delete parent.API_1484_11;  } } catch {}
    };
  }, []);

  const statusColor = {
    'passed': '#ccff00',
    'completed': '#00FFFF',
    'failed': '#ff4444',
    'incomplete': '#FF00FF',
    'not attempted': 'rgba(255,255,255,0.4)',
  }[status] || 'rgba(255,255,255,0.4)';

  if (!launchData) return (
    <div style={{ minHeight: '100vh', background: '#060810', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <Loader2 style={{ width: 40, height: 40, color: '#00FFFF', animation: 'spin 1s linear infinite' }} />
    </div>
  );

  return (
    <div style={{ minHeight: '100vh', background: '#060810', display: 'flex', flexDirection: 'column' }}>
      {/* Barra superior */}
      {!fullscreen && (
        <div style={{
          display: 'flex', alignItems: 'center', gap: 12,
          padding: '12px 20px',
          background: 'rgba(0,0,0,0.6)',
          borderBottom: '1px solid rgba(255,255,255,0.08)',
          backdropFilter: 'blur(12px)',
          zIndex: 10,
        }}>
          <button onClick={() => navigate(-1)} style={{
            display: 'flex', alignItems: 'center', gap: 6,
            color: '#00FFFF', background: 'none', border: 'none',
            fontSize: 11, fontWeight: 800, textTransform: 'uppercase',
            letterSpacing: '0.1em', cursor: 'pointer'
          }}>
            <ChevronLeft size={16} /> Volver
          </button>

          <div style={{ width: 1, height: 20, background: 'rgba(255,255,255,0.1)' }} />

          <span style={{ color: 'rgba(255,255,255,0.8)', fontSize: 13, fontWeight: 700, flex: 1 }}>
            {launchData.title || 'Módulo SCORM'}
          </span>

          {/* Estado */}
          <div style={{
            display: 'flex', alignItems: 'center', gap: 6,
            padding: '4px 10px', borderRadius: 6,
            background: 'rgba(255,255,255,0.05)',
            border: `1px solid ${statusColor}30`,
          }}>
            <div style={{ width: 6, height: 6, borderRadius: '50%', background: statusColor, boxShadow: `0 0 8px ${statusColor}` }} />
            <span style={{ color: statusColor, fontSize: 10, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.1em' }}>
              {status}
            </span>
          </div>

          <button onClick={() => setFullscreen(f => !f)} style={{
            color: 'rgba(255,255,255,0.5)', background: 'none', border: 'none', cursor: 'pointer', padding: 4
          }}>
            <Maximize2 size={16} />
          </button>
        </div>
      )}

      {/* Iframe SCORM */}
      <div style={{ flex: 1, position: 'relative', display: 'flex' }}>
        {loading && (
          <div style={{
            position: 'absolute', inset: 0, display: 'flex', alignItems: 'center',
            justifyContent: 'center', background: '#060810', zIndex: 5
          }}>
            <div style={{ textAlign: 'center' }}>
              <Loader2 style={{ width: 40, height: 40, color: '#00FFFF', margin: '0 auto 12px' }} />
              <p style={{ color: '#00FFFF', fontSize: 11, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.1em' }}>
                Inicializando SCORM...
              </p>
            </div>
          </div>
        )}

        {fullscreen && (
          <button onClick={() => setFullscreen(false)} style={{
            position: 'absolute', top: 12, right: 12, zIndex: 10,
            background: 'rgba(0,0,0,0.7)', border: '1px solid rgba(255,255,255,0.2)',
            borderRadius: 8, padding: '6px 10px', color: '#fff', cursor: 'pointer',
            display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, fontWeight: 700
          }}>
            <Minimize2 size={14} /> Salir
          </button>
        )}

        <iframe
          ref={iframeRef}
          src={`${process.env.REACT_APP_API_URL}${launchData.launch_url}`}
          onLoad={injectScormApi}
          style={{
            width: '100%',
            height: fullscreen ? '100vh' : 'calc(100vh - 57px)',
            border: 'none',
            display: 'block',
          }}
          allow="fullscreen"
          title="SCORM Content"
        />
      </div>
    </div>
  );
};

export default ScormPlayer;